/**
 * カートボタン状態管理のビジネスロジック（純粋関数）
 *
 * DOM操作・副作用を含まない。Vitestでテスト可能。
 */

/** バリアントのカート関連状態 */
export interface VariantCartState {
  available: boolean;
  inventoryQuantity: number;
  inventoryManagement: string | null;
  price: number;
  /**
   * 会員価格スロット（税込・送料込、円×100）。
   * 通常は一般会員価格。寄付会員ログイン時は donation 価格を入れる
   * （snippets/v2-product-cart.liquid のマップ生成で SSR 時に切替）。
   */
  priceGeneral: number;
  /**
   * プレミアム会員価格（税込・送料込、円×100）。
   * _premium 派生 metafield 未設定の variant、および寄付会員ログイン時は null
   * （card 仕様統一でセクション hidden）。
   */
  pricePremium: number | null;
  unitPrice: number | null;
  unitName: string | null;
  /** product.metafields.info.max_quantity（未設定なら null） */
  maxPerOrder: number | null;
  /** このバリアントが既にカートに入っている数量 */
  inCartQuantity: number;
}

/** 全バリアントの状態マップ（キー: バリアントID） */
export type VariantStateMap = Record<string, VariantCartState>;

/**
 * カートボタンを disabled にすべきか判定する
 *
 * - available でない → disabled
 * - 在庫追跡あり（inventoryManagement != null）かつ inventoryQuantity <= 0 → disabled
 * - 在庫追跡なし（ギフトカード等）は inventoryQuantity に関係なく有効
 *
 * @param state - バリアントの在庫状態
 * @returns true の場合ボタンを disabled にする
 */
export function isButtonDisabled(state: VariantCartState): boolean {
  if (!state.available) return true;
  if (state.inventoryManagement && state.inventoryQuantity <= 0) return true;
  return false;
}

/**
 * ボタンテキストの i18n キーを返す
 *
 * @param disabled - ボタンが disabled かどうか
 * @returns window.variantStrings のキー名
 */
export function getButtonTextKey(
  disabled: boolean,
): 'addToCart' | 'soldOut' {
  return disabled ? 'soldOut' : 'addToCart';
}

/**
 * 1 注文あたりの既定上限数。
 * metafield info.max_quantity が未設定の商品に適用する（v1 QuantityInput の maxQuantity 既定値）。
 *
 * ★同値 10 が以下の計 4 箇所に存在する。Liquid は TS 定数を import できず、
 *   バンドル境界を跨ぐ import は成果物の再チャンク（多数バンドルのハッシュ変動）を招くため
 *   意図的に個別定義とし、値変更時は 4 箇所すべてを同時に更新すること:
 *     1. 本定数 DEFAULT_MAX_PER_ORDER（PDP の JS 上限）
 *     2. src-lit/components/cart/kd-cart-quantity.ts の `max`（カート data-max 欠落時の JS フォールバック）
 *     3. snippets/v2-product-cart.liquid（PDP の SSR max_qty）
 *     4. snippets/v2-cart-max-quantity.liquid（カートの SSR max_quantity・SSoT）
 */
const DEFAULT_MAX_PER_ORDER = 10;

/**
 * 1 注文あたりの実効最大数を返す。
 *
 * 在庫追跡なし（ギフトカード等）は max を計算する意味がないため null を返す。
 * 在庫追跡ありの場合は `min(cap, inventoryQuantity)` を返す。
 * cap は maxPerOrder（正のとき）、未設定 / 0 以下なら既定 10（DEFAULT_MAX_PER_ORDER）。
 */
export function computeEffectiveMax(state: VariantCartState): number | null {
  if (!state.inventoryManagement) return null;
  const inv = state.inventoryQuantity;
  const cap =
    state.maxPerOrder != null && state.maxPerOrder > 0
      ? state.maxPerOrder
      : DEFAULT_MAX_PER_ORDER;
  return Math.min(cap, inv);
}

/**
 * カート内の数量を加味した「これ以上注文できる数」を返す。
 * effective max が null（在庫追跡なし）の場合は null。
 * 既に max に達している / 超えている場合は 0 を返す。
 */
export function computePurchasableMax(state: VariantCartState): number | null {
  const effective = computeEffectiveMax(state);
  if (effective == null) return null;
  return Math.max(0, effective - state.inCartQuantity);
}

/**
 * 価格を日本円フォーマットに変換する
 *
 * Shopify の price は通貨の最小単位 × 100（JPY でも × 100）で格納されている。
 * 例: ¥2,980 → 298000
 *
 * @param price - 価格（Shopify の price 単位）
 * @returns "1,234" 形式の文字列
 */
export function formatPrice(price: number): string {
  return Math.floor(price / 100).toLocaleString('ja-JP');
}
